import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import "./UserCommonPopup.css";
import "./UserCreatePopup.css";
import InputContainer from "../../../../components/Util/InputContainer";
import Swal from "sweetalert2";
import axios from "axios";
import { getServerBaseUrl } from "../../../../utils/portConfig";

function UserModifyPopup({ isOpen, onClose, onSave, selectedUser, onResetPassword, onDelete }) {
  // 초기 사용자 데이터 상태
  const initialUserData = {
    userid: "",
    userName: selectedUser ? selectedUser.username : "",
    newPassword: "",
    confirmPassword: "",
    comment: "",
    nickname: selectedUser ? selectedUser.nickname : undefined,
    hospital: selectedUser ? selectedUser.hospitalId : 0,
    auth: selectedUser ? selectedUser.roleId : 1,
    enabled: selectedUser ? selectedUser.enabled : true,
    profileImg: selectedUser ? selectedUser.profileImg : "",
    birthDate: selectedUser ? selectedUser.birthDate : "",
    telNumber: selectedUser ? selectedUser.telNumber : "",
  };
  // 사용자 데이터 상태와 관련된 상태 변수 및 함수
  const [userData, setUserData] = useState({ ...initialUserData });
  const [usernameValidationMessage, setUsernameValidationMessage] = useState("");
  const [passwordValidationMessage, setPasswordValidationMessage] = useState("");
  const [nicknameValidationMessage, setnicknameValidationMessage] = useState("");

  const [hospitalOptions, setHospitalOptions] = useState([]);
  const [isDeleteProfileImg, setIsDeleteProfileImg] = useState(false);
  const [newProfileImage, setNewProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const API_SERVER_URL = `${getServerBaseUrl()}/api/v1`;
  const token = localStorage.getItem("token");
  const id = localStorage.getItem("id");

  const roleSelectOptions = [
    { label: "시스템관리자", value: 1 },
    { label: "병원관리자", value: 2 },
    { label: "의사", value: 3 },
    { label: "간호사", value: 4 },
  ];

  if (selectedUser && selectedUser.roleId === 5) {
    roleSelectOptions.push({ label: "환자", value: 5 });
  }

  // 생년월일 형식 포맷팅 함수 추가
  const formatBirthDate = (birthDate) => {
    if (!birthDate) return "";

    // 이미 하이픈이 있는 경우 그대로 반환
    if (birthDate.includes("-")) return birthDate;

    // 8자리 숫자인 경우 YYYY-MM-DD 형식으로 변환
    if (birthDate.length === 8) {
      return `${birthDate.substring(0, 4)}-${birthDate.substring(4, 6)}-${birthDate.substring(6, 8)}`;
    }

    return birthDate; // 다른 형식은 그대로 반환
  };

  // 입력값 변경 핸들러
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "birthDate") {
      // 사용자 입력 시 숫자와 하이픈만 허용
      const filteredValue = value.replace(/[^\d-]/g, "");
      // 입력된 값에서 숫자만 추출
      const numericValue = filteredValue.replace(/\D/g, "");

      // 최대 8자리까지만 허용 (YYYYMMDD)
      if (numericValue.length <= 8) {
        let formattedValue = numericValue;

        // 자동 포맷팅: YYYY-MM-DD
        if (numericValue.length > 4) {
          formattedValue = `${numericValue.substring(0, 4)}-${numericValue.substring(4, Math.min(6, numericValue.length))}`;
        }

        if (numericValue.length > 6) {
          formattedValue = `${numericValue.substring(0, 4)}-${numericValue.substring(4, 6)}-${numericValue.substring(6, 8)}`;
        }

        setUserData({
          ...userData,
          [name]: formattedValue,
        });
      }
    } else if (name === "telNumber") {
      // 휴대폰 번호 형식(000-0000-0000)만 허용
      const rawValue = value.replace(/[^0-9]/g, "");
      let formattedValue = "";

      // 최대 11자리까지만 입력 허용
      if (rawValue.length <= 11) {
        // 휴대폰 번호 형식(010, 011, 016, 017, 018, 019로 시작)
        if (rawValue.startsWith("010") || rawValue.startsWith("011") || rawValue.startsWith("016") || rawValue.startsWith("017") || rawValue.startsWith("018") || rawValue.startsWith("019")) {
          // 자동 포맷팅
          if (rawValue.length <= 3) {
            formattedValue = rawValue;
          } else if (rawValue.length <= 7) {
            formattedValue = `${rawValue.substring(0, 3)}-${rawValue.substring(3)}`;
          } else {
            formattedValue = `${rawValue.substring(0, 3)}-${rawValue.substring(3, 7)}-${rawValue.substring(7)}`;
          }

          setUserData({
            ...userData,
            [name]: formattedValue,
          });
        } else if (rawValue.length <= 3) {
          // 아직 3자리 미만이면 일단 입력 허용 (010 등의 시작 번호를 입력 중일 수 있음)
          setUserData({
            ...userData,
            [name]: rawValue,
          });
        }
      }
    } else if (name === "newPassword" || name === "confirmPassword") {
      // 비밀번호 필드에서 공백 제거
      const noSpaceValue = value.replace(/\s/g, "");
      setUserData({
        ...userData,
        [name]: noSpaceValue,
      });
    } else if (name === "nickname" || name === "userName") {
      setnicknameValidationMessage("");
      if (value.length <= 20) {
        setUserData({
          ...userData,
          [name]: value,
        });
      }
    } else {
      // 다른 필드는 기존 방식으로 처리
      setUserData({
        ...userData,
        [name]: value,
      });
    }
  };

  // 전화번호 형식 포맷팅 함수
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return "";

    // 이미 하이픈이 있는 경우 제거
    const cleaned = phoneNumber.replace(/-/g, "");

    if (cleaned.length === 11) {
      return `${cleaned.substring(0, 3)}-${cleaned.substring(3, 7)}-${cleaned.substring(7)}`;
    } else if (cleaned.length === 10) {
      return `${cleaned.substring(0, 3)}-${cleaned.substring(3, 6)}-${cleaned.substring(6)}`;
    }
    return cleaned; // 포맷팅할 수 없는 경우 원래 값 반환
  };

  // 팝업이 열릴 때 초기 데이터 설정
  useEffect(() => {
    if (isOpen) {
      setUserData({
        userid: selectedUser ? selectedUser.userid : "",
        userName: selectedUser ? selectedUser.username : "",
        nickname: selectedUser ? selectedUser.nickname : "",
        newPassword: "",
        confirmPassword: "",
        comment: selectedUser ? selectedUser.comment : "",
        hospital: selectedUser ? selectedUser.hospitalId : 0,
        auth: selectedUser ? selectedUser.roleId : 1,
        enabled: selectedUser ? selectedUser.enabled : true,
        // birthDate: selectedUser ? selectedUser.birthDate : "",
        birthDate: selectedUser ? formatBirthDate(selectedUser.birthDate) : "",
        // 전화번호 데이터 가져올 때 포맷팅 적용
        telNumber: selectedUser ? formatPhoneNumber(selectedUser.telNumber) : "",
      });
      setUsernameValidationMessage("");
      setnicknameValidationMessage("");
      setPasswordValidationMessage("");
      setNewProfileImage(null);
      setImagePreview(null);
      setIsDeleteProfileImg(false);
      setImagePreview(selectedUser?.profileImg?.thumbnailUrl || null); // 프로필 이미지 미리보기 설정
      setHospitalOptions([]);
    }
  }, [isOpen, selectedUser]);

  // 비밀번호 확인 이벤트 핸들러
  const handleBlur = () => {
    if (userData.confirmPassword) {
      if (userData.newPassword !== userData.confirmPassword) {
        setPasswordValidationMessage("비밀번호가 일치하지 않습니다.");
      } else {
        setPasswordValidationMessage("비밀번호가 일치합니다.");
      }
    } else {
      setPasswordValidationMessage("");
    }
  };

  const handleUsernameBlur = () => {
    /// 최소 1자, 최대 20자
    /// 특수문자 및 숫자 입력 제한 (한글/영문만 허용)
    /// 전체 공백 또는 맨앞/맨뒤 공백만 입력된 값은 허용하지 않음
    /// 이름 중간에 한 칸 이내의 공백만 허용 가능
    const username = userData.userName?.trim() || "";
    const regex = /^[a-zA-Z가-힣]+$|^[a-zA-Z가-힣]+ [a-zA-Z가-힣]+$/; // 한글, 영어, 공백(중간에 한 칸 이내)만 허용
    if (username.length === 0) {
      setUsernameValidationMessage("사용자명을 입력해주세요.");
      return;
    } else if (!regex.test(username)) {
      setUsernameValidationMessage("한글, 영어만 입력할 수 있어요. (중간에 한 칸 이내의 공백 허용)");
      return;
    } else if (username.length < 1 || username.length > 20) {
      setUsernameValidationMessage("사용자명은 1자 이상 20자 이하로 입력해주세요.");
      return;
    } else {
      setUsernameValidationMessage("");
    }
  };

  const handleNicknameBlur = () => {
    //이전값과 동일한 경우 패스
    if (initialUserData.nickname === userData.nickname || (initialUserData.nickname === null && userData.nickname === "")) {
      return;
    }
    /// 최소 1자, 최대 20자
    /// 특수문자 및 숫자 입력 제한 (한글/영문만 허용)
    /// 전체 공백 또는 맨앞/맨뒤 공백만 입력된 값은 허용하지 않음
    /// 이름 중간에 한 칸 이내의 공백만 허용 가능
    const nickname = userData.nickname?.trim() || "";
    const regex = /^[a-zA-Z가-힣]+$|^[a-zA-Z가-힣]+ [a-zA-Z가-힣]+$/; // 한글, 영어, 공백(중간에 한 칸 이내)만 허용

    if (!regex.test(nickname)) {
      setnicknameValidationMessage("한글, 영어, 숫자만 입력할 수 있어요.(중간에 한 칸 이내의 공백 허용)");
      return;
    } else if (nickname.length === 0) {
      setnicknameValidationMessage("");
    } else if (nickname.length < 2 || nickname.length > 20) {
      setnicknameValidationMessage("닉네임은 2자 이상 20자 이하로 입력해주세요.");
      return;
    }
  };

  // 저장 버튼 클릭 이벤트 핸들러
  const handleSaveClick = async () => {
    if (nicknameValidationMessage !== "" || (initialUserData.nickname && userData.nickname === "")) {
      Swal.fire("알림", "닉네임을 올바르게 입력해주세요.", "warning");
      return;
    }

    if (usernameValidationMessage !== "" || (initialUserData.userName && userData.userName === "")) {
      Swal.fire("알림", "사용자명을 올바르게 입력해주세요.", "warning");
      return;
    }

    if (Number(userData.hospital) === 0 && (Number(userData.auth) === 2 || Number(userData.auth) === 3)) {
      Swal.fire("알림", "소속 병원을 선택해 주세요.", "warning");
      return;
    }

    if (userData.newPassword !== userData.confirmPassword) {
      setPasswordValidationMessage("비밀번호가 일치하지 않습니다.");
      return;
    }

    if (userData.newPassword && !userData.confirmPassword) {
      Swal.fire("알림", "비밀번호 확인을 입력해 주세요.", "warning");
      return;
    }

    // 생년월일 형식 검증
    if (selectedUser && selectedUser.roleId === 5 && userData.birthDate) {
      const birthDateWithoutHyphens = userData.birthDate.replace(/-/g, "");

      if (birthDateWithoutHyphens.length !== 8) {
        Swal.fire("알림", "생년월일 형식이 올바르지 않습니다. (예: YYYY-MM-DD)", "warning");
        return;
      }
    }

    // 전화번호 형식 검증
    if (selectedUser && selectedUser.roleId === 5 && userData.telNumber) {
      // 휴대폰 번호 형식만 체크 (010-0000-0000 등)
      const mobileRegex = /^01[0|1|6|7|8|9]-\d{3,4}-\d{4}$/;
      if (!mobileRegex.test(userData.telNumber)) {
        Swal.fire("알림", "휴대폰 번호 형식이 올바르지 않습니다. (예: 010-0000-0000)", "warning");
        return;
      }
    }

    if (passwordValidationMessage !== "비밀번호가 일치하지 않습니다.") {
      userData.id = selectedUser.id;
      userData.links = selectedUser.links;

      // 생년월일이 하이픈 형식이 아니라면 하이픈 추가
      let formattedBirthDate = userData.birthDate || undefined;
      if (formattedBirthDate && !formattedBirthDate.includes("-")) {
        formattedBirthDate = formatBirthDate(formattedBirthDate);
      }

      // 전화번호가 하이픈 형식이 아니라면 하이픈 추가
      let formattedTelNumber = userData.telNumber || undefined;
      if (formattedTelNumber && !formattedTelNumber.includes("-")) {
        formattedTelNumber = formatPhoneNumber(formattedTelNumber);
      }

      const userMoJson = {
        id: selectedUser.id,
        links: selectedUser.links,
        userId: userData.userid,
        userName: userData.userName.trim(),
        nickname: userData.nickname?.trim() ? userData.nickname.trim() : null,
        comment: userData.comment?.trim() ? userData.comment.trim() : undefined,
        hospitalId: userData.hospital,
        roleId: userData.auth,
        enabled: userData.enabled,
        password: userData.confirmPassword ? userData.newPassword : undefined,
        birthDate: formattedBirthDate, // 하이픈 포함된 형식으로 전송
        telNumber: formattedTelNumber, // 하이픈 포함된 형식으로 전송
        profileImg: newProfileImage,
      };
      // Ask for confirmation before saving user edits
      const confirmationResult = await Swal.fire({
        icon: "question",
        title: "사용자 수정 저장",
        text: "사용자를 수정하시겠습니까?",
        showCancelButton: true, // Add cancel button
        confirmButtonText: "확인", // Confirm button text
        cancelButtonText: "취소", // Cancel button text
      });

      if (confirmationResult.isConfirmed) {
        try {
          if (selectedUser.profileImg !== null && (isDeleteProfileImg || newProfileImage)) {
            await deleteUserProfile();
          }
          await onSave(userMoJson);
        } catch (error) {
          console.error("Error during user update:", error);
        }
      }
    } else {
      Swal.fire("알림", "비밀번호가 일치하지 않습니다.", "warning");
    }
  };

  // 병원 옵션 가져오는 함수
  const fetchHospitals = async () => {
    try {
      const response = await axios.get(`${API_SERVER_URL}/hospital/dropdownList/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 200) {
        setHospitalOptions((prevOptions) => [...prevOptions, ...response.data]);
      } else {
        Swal.fire("오류", "병원 목록을 가져오는데 실패했습니다.", "error");
      }
    } catch (error) {
      console.error(error);
    }
  };

  // 유저 프로필 이미지 삭제
  const deleteUserProfile = async () => {
    try {
      const response = await axios.delete(`${API_SERVER_URL}/users/${selectedUser.id}/deleteProfileImg`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 204) {
        setNewProfileImage(null);
        setImagePreview(null);
      } else {
        await Swal.fire({
          icon: "error",
          title: "실패",
          text: "이미지 삭제 실패. 다시 시도해주세요.",
          confirmButtonText: "확인",
          customClass: {
            popup: "custom-swal-popup",
          },
        });
      }
    } catch (error) {
      console.error("Error deleting ProfieImg:", error);
      if (error.response) {
        const responseData = error.response.data;
        if (responseData) {
          Swal.fire({
            icon: "error",
            title: "이미지 삭제 오류",
            text: responseData,
            confirmButtonText: "확인",
            customClass: {
              popup: "custom-swal-popup",
            },
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "서버 응답 오류",
            text: `서버 응답 오류: ${error.response.status}`,
            customClass: {
              popup: "custom-swal-popup",
            },
          });
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "서버 응답이 없습니다.",
          text: "서버 응답이 없습니다.",
          customClass: {
            popup: "custom-swal-popup",
          },
        });
      }
    }
  };

  const removeUserProfile = () => {
    if (selectedUser && selectedUser.profileImg !== null) {
      setIsDeleteProfileImg(true);
    }
    setNewProfileImage(null);
    setImagePreview(null);
  };

  // 유저 프로필 이미지
  const userProfileChange = (event) => {
    const file = event.target.files[0];
    setNewProfileImage(file);

    // 이미지 미리보기 업데이트
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    if (file) {
      reader.readAsDataURL(file);
    }
    event.target.value = "";
  };

  // 팝업이 열릴 때 병원 데이터 가져오기
  useEffect(() => {
    if (isOpen) {
      fetchHospitals();
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="User Popup"
      style={{
        overlay: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 1000,
        },
        content: {
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          border: "1px solid #ccc",
          padding: "15px",
          background: "white",
          borderRadius: "4px",
          width: "400px",
          height: "658px",
        },
      }}
    >
      <div className="modal-header-close-button" onClick={onClose}>
        x
      </div>
      <div className="user-modal-content">
        <div className="user-modal-header">
          <h2>사용자 상세정보</h2>
        </div>
        <div className="input-container" style={{ marginBottom: "10px" }}>
          <div className="input-row">
            <label className="move-center">사용자 프로필</label>
            <div className="input-field move-img-container-centner">
              <div className="selected-image-preview">
                {!newProfileImage && !imagePreview && (
                  <span className="img-upload-text">
                    이미지를 업로드하세요.
                    <br />
                    권장: 650 x 650
                  </span>
                )}
                {(newProfileImage || imagePreview) && (
                  <>
                    <img src={imagePreview || URL.createObjectURL(newProfileImage)} alt="Selected Preview" />
                    <span className="remove-image-button" onClick={removeUserProfile}>
                      X
                    </span>
                  </>
                )}
              </div>
              <div className="file-upload-style">
                <input
                  id="fileInput"
                  type="file"
                  accept="images/*"
                  onChange={userProfileChange}
                  style={{ display: "none" }} // 실제 파일 업로드를 숨기기 위해 display 속성을 사용합니다.
                />
                <label htmlFor="fileInput" className="custom-file-upload">
                  <span className="upload-img-icon">🔄</span> 이미지 바꾸기
                </label>
              </div>
            </div>
          </div>
        </div>

        <InputContainer
          autoComplete="off"
          label="사용자 ID"
          name="uid"
          value={userData.userid ?? ""}
          inputReadonly={true}
          inputStyle={{ backgroundColor: "#f0f0f0" }} // Readonly 스타일 적용
        />
        <div className="user-tie-container">
          <InputContainer
            autoComplete="off"
            label="사용자명"
            name="userName"
            value={userData.userName}
            onChange={handleChange}
            validationMessage={usernameValidationMessage === "" ? <span>&nbsp;</span> : usernameValidationMessage}
            onBlur={handleUsernameBlur}
            validationColor={"red"}
          />

          {/* 닉네임 */}
          {selectedUser && selectedUser.roleId !== 4 && (
            <InputContainer
              autoComplete="off"
              label="사용자 닉네임"
              name="nickname"
              value={userData.nickname ?? ""}
              onChange={handleChange}
              onBlur={handleNicknameBlur}
              validationMessage={nicknameValidationMessage === "" ? <span>&nbsp;</span> : nicknameValidationMessage}
              validationColor={"red"}
            />
          )}
        </div>
        {selectedUser && selectedUser.roleId === 5 && (
          <div className="user-tie-container">
            <div className="input-container">
              <div className="input-row" style={{ display: "flex", flexDirection: "column" }}>
                <label htmlFor="userBirth">생년월일</label>
                <input autoComplete="off" type="text" name="birthDate" value={userData.birthDate} onChange={handleChange} placeholder="YYYY-MM-DD" maxLength={10} />
              </div>
            </div>
            <div className="input-container">
              <div className="input-row" style={{ display: "flex", flexDirection: "column" }}>
                <label htmlFor="userTelNo">휴대폰 번호</label>
                <input autoComplete="off" type="text" name="telNumber" value={userData.telNumber} onChange={handleChange} placeholder="010-0000-0000" />
              </div>
            </div>
          </div>
        )}
        <div className="user-tie-container">
          <InputContainer autoComplete="off" passwordMask={true} label="새 비밀번호" name="newPassword" value={userData.newPassword} onChange={handleChange} validationMessage={<span>&nbsp;</span>} />
          <InputContainer
            autoComplete="off"
            passwordMask={true}
            label="비밀번호 확인"
            name="confirmPassword"
            value={userData.confirmPassword}
            onChange={handleChange}
            onBlur={handleBlur}
            validationMessage={passwordValidationMessage === "" ? <span>&nbsp;</span> : passwordValidationMessage}
            validationColor={passwordValidationMessage === "비밀번호가 일치하지 않습니다." ? "red" : "green"}
          />
        </div>
        <div className="user-tie-container">
          <InputContainer label="권한" id="modifyAuth" name="auth" value={userData.auth} onChange={handleChange} type="select" options={roleSelectOptions} />
          <InputContainer
            label="사용유무"
            name="enabled"
            value={userData.enabled ? "Y" : "N"}
            onChange={(e) => {
              const newValue = e.target.value;
              setUserData({
                ...userData,
                enabled: newValue === "Y",
              });
            }}
            type="select"
            options={[
              { label: "사용", value: "Y" },
              { label: "미사용", value: "N" },
            ]}
          />
        </div>
        <InputContainer
          label="병원"
          name="hospital"
          value={userData.hospital}
          onChange={handleChange}
          type="select"
          options={[{ label: "소속병원 없음", value: 0 }, ...hospitalOptions.map((hospital) => ({ label: hospital.hospitalLabel, value: hospital.id }))]}
        />
        {selectedUser && selectedUser.roleId !== 5 && (
          <div className="input-container">
            <div className="input-row">
              <label>사용자 소개</label>
              <div className="input-field user-introduce-style">
                <textarea name="comment" value={userData.comment} placeholder="소개를 입력하세요." onChange={handleChange} maxLength={500} />
              </div>
            </div>
          </div>
        )}
        <div className="user-button-container">
          <button className="dashboard-content-button popup-button" onClick={handleSaveClick}>
            수정
          </button>
          <button className={`dashboard-content-button yellow-button popup-button`} onClick={onResetPassword}>
            비밀번호 초기화
          </button>
          <button className="dashboard-content-button red-button popup-button" onClick={onDelete}>
            삭제
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default UserModifyPopup;
